import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import moment from "moment";
import ProjectChat from "../../../_shared/ProjectChat/ProjectChat";
import StandardButton from "../../../../shared/formComponents/StandardButton/StandardButton";
import * as xrayActions from "../../_redux/xrayActions";
import "./xray-chat-page.scss";

const getInitials = (respondent) =>
  `${respondent?.firstName?.[0] || ""}${respondent?.lastName?.[0] || ""}`.toUpperCase();

// Fetching a stored conversation from the backend isn't the agent
// "thinking" — that's ProjectChat/ProjectLoader's animation for a live
// response being generated, which doesn't apply here. This renders a
// handful of skeleton bubbles (alternating sides, shimmering) so it reads
// as "loading a chat history" instead.
const ChatSkeletonLoader = () => {
  const rows = [
    { side: "left", width: "62%" },
    { side: "right", width: "40%" },
    { side: "left", width: "75%" },
    { side: "left", width: "50%" },
    { side: "right", width: "58%" },
  ];
  return (
    <div className="chat-skeleton-loader">
      {rows.map((row, index) => (
        <div
          key={index}
          className={`chat-skeleton-row d-flex ${
            row.side === "right"
              ? "justify-content-end"
              : "justify-content-start"
          }`}
        >
          <div className="chat-skeleton-bubble" style={{ width: row.width }} />
        </div>
      ))}
    </div>
  );
};

// Opened in a new tab from the "View" button on the X-Ray score page's AI
// conversations card, so the coach keeps the score/recommendations tab open
// behind it. The left column repeats the same respondent list shown under
// the BMI card on the score page, so the coach can jump straight from one
// person's conversation to another's without leaving this tab.
//
// This is a brand-new tab/page load, so there's no shared Redux state with
// the score page tab — the surveyId (x_ray_id) and respondent list are
// re-fetched from scratch here using the SME's userId from the route.
export const XrayChatPage = () => {
  const history = useHistory();
  const params = useParams();
  const dispatch = useDispatch();

  const [respondents, setRespondents] = useState(null);
  const [selectedRespondentId, setSelectedRespondentId] = useState(
    params.respondentId
  );
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  // This page renders inside CoachPanel's persistent layout (below the app
  // Header, inside a chain of plain wrapper divs that don't give it a
  // definite height to work with via CSS alone) — so instead of guessing a
  // pixel offset for the Header, measure the real remaining viewport height
  // in JS and pin this page to exactly that, rather than trusting `100%`/
  // `100vh` to resolve correctly through ancestors we don't control.
  const pageRef = useRef(null);
  const [pageHeight, setPageHeight] = useState(null);

  useEffect(() => {
    const updateHeight = () => {
      if (!pageRef.current) return;
      const top = pageRef.current.getBoundingClientRect().top;
      setPageHeight(window.innerHeight - top);
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [respondents, selectedRespondentId]);

  // Fresh tab/page load — no shared Redux state with the score page tab, so
  // surveyId (x_ray_id) is re-fetched here via the SME's userId from the route.
  const surveyId = useSelector((state) => state.xrayReport.xRayState.surveyId);

  useEffect(() => {
    dispatch(xrayActions.getXrayStatus(params.userId));
  }, [params.userId]);

  useEffect(() => {
    if (!surveyId) return;
    dispatch(xrayActions.getXrayChatSummary(surveyId)).then((summary) => {
      setRespondents(
        (summary || []).map((r) => ({
          userId: r.userId,
          firstName: r.firstName,
          lastName: r.lastName,
          isActive: r.isActive,
          chatUniqueId: r.chatUniqueId,
          chatMessageCount: parseInt(r.chatMessageCount, 10) || 0,
          lastActiveLabel: r.lastActiveAt
            ? moment(r.lastActiveAt).format("D MMM")
            : null,
        }))
      );
    });
  }, [surveyId]);

  const selectedRespondent = (respondents || []).find(
    (r) => r.userId === selectedRespondentId
  );

  useEffect(() => {
    // Clear immediately on switch, before the fetch resolves — otherwise the
    // previous respondent's messages stay on screen with the loader tacked
    // on below them, which reads as "nothing happened" rather than loading.
    setMessages([]);
    if (!selectedRespondent?.chatUniqueId) {
      setMessagesLoading(false);
      return;
    }
    setMessagesLoading(true);
    dispatch(xrayActions.getXrayChatMessages(selectedRespondent.chatUniqueId))
      .then((msgs) => setMessages(msgs || []))
      .finally(() => setMessagesLoading(false));
  }, [selectedRespondent?.chatUniqueId]);

  if (!respondents) {
    return <div className="xray-chat-page d-flex" ref={pageRef} />;
  }

  // Same grouping/ordering as the AI conversations card: anyone with a chat
  // to view first (active, then deactivated), active non-chatters last.
  // Scenario 4 (deactivated + never chatted) is excluded entirely.
  const visibleRespondents = respondents.filter(
    (r) => r.isActive || r.chatMessageCount > 0
  );
  const activeChatted = visibleRespondents.filter(
    (r) => r.isActive && r.chatMessageCount > 0
  );
  const deactivatedChatted = visibleRespondents.filter(
    (r) => !r.isActive && r.chatMessageCount > 0
  );
  const activeNoChat = visibleRespondents.filter(
    (r) => r.isActive && r.chatMessageCount === 0
  );
  const orderedRespondents = [
    ...activeChatted,
    ...deactivatedChatted,
    ...activeNoChat,
  ];

  const selectRespondent = (respondent) => {
    if (respondent.chatMessageCount === 0) return;
    setSelectedRespondentId(respondent.userId);
    history.replace(
      history.location.pathname.replace(
        /\/chat\/[^/]+$/,
        `/chat/${respondent.userId}`
      )
    );
  };

  if (!selectedRespondent) {
    return (
      <div
        className="xray-chat-page d-flex align-items-center justify-content-center"
        ref={pageRef}
        style={pageHeight ? { height: pageHeight } : undefined}
      >
        <span className="dark-grey">Conversation not found.</span>
      </div>
    );
  }

  return (
    <div
      className="xray-chat-page d-flex"
      ref={pageRef}
      style={pageHeight ? { height: pageHeight } : undefined}
    >
      <div className="respondent-switcher">
        <div className="switcher-top d-flex align-items-center">
          <StandardButton
            icon={<span className="material-icons icon">arrow_back</span>}
            text="Back"
            onClick={() => {
              // This page always opens in a new tab (window.open), so there's
              // no prior entry in this tab's own history for goBack() to
              // reach — the score page tab is already open behind it, so
              // "Back" here means close this tab and return to it.
              window.close();
            }}
            reversed
          />
          <span className="switcher-title ms-2">AI conversations</span>
        </div>
        <div className="switcher-list">
          {orderedRespondents.map((respondent) => {
            const chatted = respondent.chatMessageCount > 0;
            const isSelected = respondent.userId === selectedRespondentId;
            return (
              <div
                key={respondent.userId}
                className={`switcher-row d-flex align-items-center ${chatted ? "" : "no-chat"
                  } ${isSelected ? "selected" : ""}`}
                onClick={() => selectRespondent(respondent)}
              >
                <div
                  className={`initials-avatar ${!respondent.isActive ? "deactivated" : ""
                    }`}
                >
                  {getInitials(respondent)}
                </div>
                <div className="row-text flex-grow-1">
                  <div className="name">
                    {respondent.firstName} {respondent.lastName}
                    {!respondent.isActive ? (
                      <span className="deactivated-tag">Deactivated</span>
                    ) : null}
                  </div>
                  <div className="subtext">
                    {chatted
                      ? `${respondent.chatMessageCount} msgs, last active ${respondent.lastActiveLabel}`
                      : "no conversation yet"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="chat-page-main d-flex flex-column">
        <div className="chat-page-header d-flex align-items-center">
          <div
            className={`initials-avatar ${!selectedRespondent.isActive ? "deactivated" : ""
              }`}
          >
            {getInitials(selectedRespondent)}
          </div>
          <div className="ms-2">
            <div className="d-flex align-items-center">
              <span className="respondent-name">
                {selectedRespondent.firstName} {selectedRespondent.lastName}
              </span>
              {!selectedRespondent.isActive ? (
                <span className="deactivated-tag ms-2">Deactivated</span>
              ) : null}
            </div>
            <div className="readonly-label">Read-only conversation</div>
          </div>
        </div>
        <div className="chat-page-body flex-grow-1">
          {messagesLoading ? (
            <ChatSkeletonLoader />
          ) : (
          <ProjectChat
            key={selectedRespondent.userId}
            messages={messages}
            loading={false}
            streamLoading={false}
            reasonLoading={false}
            showReadOnly={true}
            hideReadOnlyBanner={true}
            showInput={false}
            footerNote={
              <div className="read-only-note">
                You're viewing this conversation in read-only mode.
              </div>
            }
            renderCustomMessage={(message, index) => {
              // Full-width divider row — same senders the real SME-side
              // ChatPanel treats as "divider_xray_completed" /
              // "divider_sprint_started" / "divider_sprint_ended".
              // ProjectChat.js doesn't know about these natively (they only
              // ever appear in ai-xray/sprint chats), so this page supplies
              // the rendering via the renderCustomMessage escape hatch.
              if (
                message.sender === "divider_xray_completed" ||
                message.sender === "divider_sprint_started" ||
                message.sender === "divider_sprint_ended"
              ) {
                return (
                  <div className="chat-divider" key={index}>
                    <span className="chat-divider__line" />
                    <span className="chat-divider__label">
                      {message.message || "Entire x-ray completed"}
                    </span>
                    <span className="chat-divider__line" />
                  </div>
                );
              }
              return null;
            }}
          />
          )}
        </div>
      </div>
    </div>
  );
};

export default XrayChatPage;
